// src/components/CreateAccount.jsx

import BASE_URL from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { motion } from 'framer-motion';
import './CreateAccount.css';
import logoImg from '../assets/M2_Lab_Logo.png';
import bgImg from '../assets/Create_Account.jpg';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function CreateAccount() {
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [region, setRegion] = useState(''); // ✅ Region value

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const pwdStrong = password.length >= 8;
  const pwdMatch = confirm === password && confirm.length > 0;

  const regions = ['Americas', 'Europe', 'Asia', 'Africa', 'Oceania'];

  // Detect Chrome autofill and toggle "filled" class on text-like inputs
  useEffect(() => {
    const interval = setInterval(() => {
      if (formRef.current) {
        const inputs = formRef.current.querySelectorAll(
          'input[type="text"], input[type="email"], input[type="password"]'
        );
        inputs.forEach(input => {
          if (input.value.trim() !== '') {
            input.classList.add('filled');
          } else {
            input.classList.remove('filled');
          }
        });
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!position.trim()) {
      toast.error('Please enter your Position before submitting.');
      return;
    }
    if (!region.trim()) {
      toast.error('Please select your Region before submitting.');
      return;
    }

    const payload = {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      position,
      region, // ✅ include region
    };

    try {
      const response = await fetch(`${BASE_URL}/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success('Account created successfully!');
        setTimeout(() => navigate('/welcome'), 1500);
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (err) {
      toast.error('Server error. Please try again later.');
    }
  };

  return (
    <div className="create-page" style={{ backgroundImage: `url(${bgImg})` }}>
      <ToastContainer />
      <header className="login-navbar">
        <div className="login-logo-container" onClick={() => navigate('/')}>
          <img src={logoImg} alt="M2 Lab Logo" className="login-logo-img" />
          <span className="login-lab-title">Purdue M2 Lab</span>
        </div>
      </header>

      <div className="create-content">
        <motion.div
          className="create-box"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <h2 className="create-heading">Join SynthGuard</h2>
          <form onSubmit={handleSubmit} className="create-form" ref={formRef}>
            <div className="input-group">
              <input
                id="first"
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />
              <label htmlFor="first">First Name</label>
            </div>

            <div className="input-group">
              <input
                id="last"
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
              />
              <label htmlFor="last">Last Name</label>
            </div>

            <div className="input-group">
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <label htmlFor="email">Email Address</label>
            </div>

            <div className="input-group">
              <input
                id="position"
                type="text"
                value={position}
                onChange={e => setPosition(e.target.value)}
                required
              />
              <label htmlFor="position">Position</label>
            </div>

            {/* ✅ Region radio group (styled pills) */}
            <fieldset className="radio-group">
              <legend className="radio-label">Region</legend>
              <div className="radio-options">
                {regions.map((r) => (
                  <label key={r} className="radio-option">
                    <input
                      type="radio"
                      name="region"
                      value={r}
                      checked={region === r}
                      onChange={() => setRegion(r)}
                      required
                    />
                    <span>{r}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            {/* End Region radio group */}

            <div className={`input-group ${pwdStrong ? 'strong' : 'weak'}`}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <label htmlFor="password">Password</label>
              <span
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </span>
            </div>

            <div className={`input-group ${pwdMatch ? 'match' : 'no-match'}`}>
              <input
                id="confirm"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
              />
              <label htmlFor="confirm">Confirm Password</label>
              <span
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </span>
            </div>

            <button
              type="submit"
              className="create-button"
              disabled={!pwdStrong || !pwdMatch}
            >
              Create Account
            </button>
          </form>
        </motion.div>
      </div>

      <footer className="login-footer">© 2025 Purdue University</footer>
    </div>
  );
}
